import { defineMessages, type IntlShape } from 'react-intl';

import { fg } from '@confluence/feature-gating';

import type { SnippetOwner } from '../types/snippetOwner.ts';
import { ATLASSIAN_ACCOUNT_ID } from '../constants/snippetOwner.ts';
import type { Snippet } from '../types/snippet.ts';

const messages = defineMessages({
	snippetBy: {
		id: 'snippets.quick-insert.by',
		defaultMessage: 'by {name}',
		description: 'Description text showing who created the snippet, with the creator name',
	},
	template: {
		id: 'snippets.quick-insert.template',
		defaultMessage: 'template',
		description: 'Keyword for template search in quick insert',
	},
	block: {
		id: 'snippets.quick-insert.block',
		defaultMessage: 'block',
		description: 'Keyword for block search in quick insert',
	},
});

/** Prefix for snippet quick insert item keys in the editor. */
const SNIPPET_QUICK_INSERT_KEY_PREFIX = 'snippet-extension:snippet-';
/**
 * Returns the quick insert menu key for a snippet. Use this when adding, updating,
 * or removing snippet items from the editor's quick insert.
 */
export const getSnippetQuickInsertKey = (snippetId: string): string =>
	`${SNIPPET_QUICK_INSERT_KEY_PREFIX}${snippetId}`;
export const extractSnippetKeywords = (name: string, intl: IntlShape): string[] => {
	const nameKeywords = name
		.split(/\s+/)
		.map((word) => word.toLowerCase().trim())
		.filter((word) => word.length > 0);

	const templateKeyword = intl.formatMessage(messages.template).toLowerCase();
	const blockKeyword = intl.formatMessage(messages.block).toLowerCase();
	const allKeywords = fg('platform_synced_block_patch_13')
		? [templateKeyword, blockKeyword]
		: [...nameKeywords, templateKeyword];
	return Array.from(new Set(allKeywords));
};

export const createSnippetDescription = (
	createdBy: SnippetOwner,
	intl: IntlShape,
	description?: string,
): string => {
	if (description && fg('platform_synced_block_patch_13')) {
		return description;
	}

	const name = createdBy.displayName || createdBy.accountId;
	return intl.formatMessage(messages.snippetBy, { name });
};

const isValidQuickInsertSnippet = (snippet: Snippet, userId?: string | null): boolean =>
	snippet.createdBy.accountId === userId ||
	snippet.createdBy.accountId === ATLASSIAN_ACCOUNT_ID ||
	snippet.isFavorite;

const getSortPriority = (snippet: Snippet, userId?: string | null): number => {
	// 1st: created by the current user
	if (snippet.createdBy.accountId === userId) {
		return 0;
	}
	// 2nd: favorited (but not created by the current user)
	if (snippet.isFavorite) {
		return 1;
	}
	// 3rd: Atlassian-created (GLOBAL scope) / others that already passed the previous filter
	return 2;
};

export const filterAndSort = (snippets: Snippet[], userId?: string | null): Snippet[] => {
	return snippets
		.filter((snippet) => isValidQuickInsertSnippet(snippet, userId))
		.sort((a, b) => getSortPriority(a, userId) - getSortPriority(b, userId));
};
